import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Navigation, A11y} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'
import {
    Box,
    Button,
    Flex,
    Heading,
    Skeleton,
    Text,
    VStack,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

import Swatch from '../../swatch-group/swatch'
import {NotifyMeEnvelope} from '../../icons'
import {FewItemsLeftIcon, FewItemsLeftMessage} from '../../few-items-left'
import ErrorMessage from '../../error-message'
import {FormattedMessage} from 'react-intl'
import * as defaultCustomStyles from '../styles.module.css'

const QuickViewVariations = ({
    styles,
    customStyles = defaultCustomStyles,
    showSkeleton,
    hasLengthVariant,
    variationAttributes,
    isDesktop,
    showOptionsMessage,
    missingAttributeMessages,
    getAttributeLabel,
    isNotifyMe,
    isFewItemsLeft,
    variant,
    getInitialSlideIndex,
    handleSwatchChange,
    notifyMeIcon,
    product,
    onOpenSizeChart,
    children,
    optionsAttempt,
    enableAutoScroll,
    isQuickAdd,
    themeVariant
}) => {
    const [hoveredColorValue, setHoveredColorValue] = useState(null)
    const sizeChartStyles = useMultiStyleConfig('SizeChartDrawer')
    const selectedQuickViewStyles = useMultiStyleConfig('QuickView', {
        variant: themeVariant,
        selected: true
    })
    const variationRefs = useRef({})
    const lastSelectedSizeRef = useRef(null)

    useEffect(() => {
        if (!enableAutoScroll) return
        if (!showOptionsMessage) return
        if (!missingAttributeMessages || !variationAttributes?.length) return

        const firstMissing = variationAttributes.find(
            ({id}) => missingAttributeMessages[id]
        )

        if (!firstMissing) return

        const target = variationRefs.current[firstMissing.id]
        if (target?.scrollIntoView) {
            target.scrollIntoView({behavior: 'smooth', block: 'start'})
        }
    }, [showOptionsMessage, missingAttributeMessages, variationAttributes, optionsAttempt])

    useEffect(() => {
        if (!enableAutoScroll) return
        if (!variationAttributes?.length) return

        const sizeAttr = variationAttributes.find(({id}) => id === 'size')
        const lengthAttr = variationAttributes.find(({id}) => id === 'length')
        const selectedSize = sizeAttr?.selectedValue?.value || null
        const selectedLength = lengthAttr?.selectedValue?.value || null
        const previousSize = lastSelectedSizeRef.current

        lastSelectedSizeRef.current = selectedSize

        if (!lengthAttr) return
        if (!selectedSize || selectedLength) return
        if (previousSize === selectedSize) return

        const target = variationRefs.current.length
        if (target?.scrollIntoView) {
            setTimeout(() => {
                target.scrollIntoView({behavior: 'smooth', block: 'start'})
            }, 0)
        }
    }, [variationAttributes])

    const hasLengthVariation = variationAttributes?.find((attr) => attr.id === 'length')
    const selectedLength = variationAttributes?.find((attr) => attr.id === 'length')?.selectedValue?.value

    const getFewItemsLeftEntry = (sizeValue) =>
        hasLengthVariation && selectedLength
            ? product?.c_fewItemsLeftArray?.find(
                  ({size, length}) => size === sizeValue && length === selectedLength
              )
            : product?.c_fewItemsLeftArray?.find(({size}) => size === sizeValue)

    return (
        <VStack {...styles.qvContainer}>
            <VStack {...styles.variationsStack}>
                {!product || showSkeleton ? (
                    <VStack
                        {...styles.skeletonStack}
                        minH={hasLengthVariant ? styles.skeletonStackMinHeightLong : styles.skeletonStackMinHeight}
                    >
                        {/* No colors on Quick Add */}
                        {!isQuickAdd && (
                            <Box {...styles.skeletonGroup}>
                                <Skeleton {...styles.skeletonLabel} />
                                <Skeleton {...styles.skeletonControlCompact} />
                            </Box>
                        )}
                        <Box {...styles.skeletonGroup}>
                            <Skeleton {...styles.skeletonLabel} />
                            <Skeleton {...styles.skeletonControl} />
                        </Box>
                        {hasLengthVariant && (
                            <Box {...styles.skeletonGroup}>
                                <Skeleton {...styles.skeletonLabel} />
                                <Skeleton {...styles.skeletonControl} />
                            </Box>
                        )}
                    </VStack>
                ) : (
                    variationAttributes.map(({id, name, selectedValue, values}) => {
                        return (
                            <Box
                                key={id}
                                ref={(node) => {
                                    if (node) variationRefs.current[id] = node
                                }}
                                {...(id === 'color' && isQuickAdd ? {} : styles.variationWrapper)}
                                width={'100%'}
                            >
                                <Flex {...styles.variationRow}>
                                    <Flex {...styles.variationLabelRow} className='{id}'>
                                        {!(showOptionsMessage && missingAttributeMessages[id]) && (
                                            <Text
                                                {...styles.variationLabel}
                                                {...(id === 'color' ? styles.colorVariationLabel : {})}
                                            >
                                                {getAttributeLabel(
                                                    id,
                                                    name,
                                                    id === 'color' && hoveredColorValue
                                                        ? hoveredColorValue
                                                        : selectedValue
                                                )}
                                            </Text>
                                        )}
                                        {!isNotifyMe &&
                                            isFewItemsLeft &&
                                            id === 'size' &&
                                            variant?.orderable && (
                                                <FewItemsLeftMessage
                                                    containerProps={
                                                        isDesktop
                                                            ? styles.fewItemsLeftDesktop
                                                            : styles.fewItemsLeftMobile
                                                    }
                                                />
                                            )}
                                        {showOptionsMessage &&
                                            missingAttributeMessages[id] && (
                                                <ErrorMessage
                                                    containerProps={
                                                        isDesktop
                                                            ? styles.errorMessageDesktop
                                                            : styles.errorMessageMobile
                                                    }
                                                    message={missingAttributeMessages[id]}
                                                />
                                            )}
                                    </Flex>
                                    {id === 'size' && !isDesktop && (
                                        <Button
                                            variant="unstyled"
                                            onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                if (onOpenSizeChart) onOpenSizeChart()
                                            }}
                                            {...sizeChartStyles.trigger}
                                        >
                                            <Text as="span" {...sizeChartStyles.trigger}>
                                                <FormattedMessage id="product_view.link.size_chart" defaultMessage="Size Chart" />
                                            </Text>
                                        </Button>
                                    )}
                                </Flex>
                                {isDesktop ? (
                                    <>
                                        {!(id === 'color') && (
                                            <>
                                                {/* Color slider */}
                                                <Swiper
                                                    key={id}
                                                    className={customStyles.carouselWrapper}
                                                    modules={[Navigation, A11y]}
                                                    spaceBetween={id === 'color' ? 0 : 4}
                                                    slidesPerView={'auto'}
                                                    navigation
                                                    initialSlide={getInitialSlideIndex(values, selectedValue, id)}
                                                    a11y={{
                                                        enabled: true,
                                                        slideRole: 'button'
                                                    }}
                                                >
                                                    {values.map(({name, image, value, orderable, online}) => {
                                                        if (id === 'color' && online === false) {
                                                            return null
                                                        }
                                                        const isSelected = selectedValue?.value === value
                                                        const availabilityTestId = `${id}-${orderable ? 'available' : 'unavailable'}`
                                                        const content = image ? (
                                                            <Box
                                                                {...styles.swatchImage}
                                                                backgroundImage={`url(${
                                                                    image.disBaseLink || image.link
                                                                })`}
                                                                data-testid={availabilityTestId}
                                                            />
                                                        ) : (
                                                            <Box
                                                                {...(isSelected
                                                                    ? selectedQuickViewStyles.swatchText
                                                                    : styles.swatchText)}
                                                            >
                                                                <Box
                                                                    as="span"
                                                                    className="swatch-content"
                                                                    data-testid={availabilityTestId}
                                                                >
                                                                    {name}
                                                                </Box>
                                                            </Box>
                                                        )
                                                        const showNotifyMeIcon = notifyMeIcon(
                                                            id,
                                                            value,
                                                            orderable
                                                        )

                                                        const fewItemsLeft = getFewItemsLeftEntry(value)

                                                        return (
                                                            <SwiperSlide
                                                                key={value}
                                                                style={styles.swiperSlide}
                                                            >
                                                                {/* Swatch for Size and Length */}
                                                                <Swatch
                                                                    isFocusable={true}
                                                                    disabled={!orderable}
                                                                    isNotifyMe={showNotifyMeIcon}
                                                                    value={value}
                                                                    name={name}
                                                                    isQuickView
                                                                    variant={id === 'color' ? 'circle' : 'square'}
                                                                    selected={isSelected}
                                                                    isQuickAdd={isQuickAdd}
                                                                    handleSelect={(v) =>
                                                                        handleSwatchChange(id, v)
                                                                    }
                                                                    id={id}
                                                                    onMouseEnter={
                                                                        id === 'color'
                                                                            ? () =>
                                                                                  setHoveredColorValue({
                                                                                      name,
                                                                                      value
                                                                                  })
                                                                            : undefined
                                                                    }
                                                                    onMouseLeave={
                                                                        id === 'color'
                                                                            ? () => setHoveredColorValue(null)
                                                                            : undefined
                                                                    }
                                                                >
                                                                    {/* envelope */}
                                                                    {showNotifyMeIcon && (
                                                                        <NotifyMeEnvelope
                                                                            {...styles.notifyMeIcon}
                                                                        />
                                                                    )}

                                                                    {!showNotifyMeIcon &&
                                                                        orderable &&
                                                                        fewItemsLeft && (
                                                                            <FewItemsLeftIcon
                                                                                {...styles.fewItemsLeftIcon}
                                                                            />
                                                                        )}
                                                                    {content}
                                                                </Swatch>
                                                            </SwiperSlide>
                                                        )
                                                    })}
                                                </Swiper>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {/* Color swatches */}
                                        <Flex className="color-swatch-grid">
                                            <Flex {...id === 'color' ? styles.colorSwatchGrid : styles.swatchGrid}>
                                                {values.map(
                                                    ({name, image, value, orderable, online}) => {
                                                        if (id === 'color' && online === false) {
                                                            return null
                                                        }
                                                        const isSelected = selectedValue?.value === value
                                                        const availabilityTestId = `${id}-${orderable ? 'available' : 'unavailable'}`
                                                        const content = image ? (
                                                            <Box
                                                                {...styles.swatchImage}
                                                                backgroundImage={`url(${
                                                                    image.disBaseLink || image.link
                                                                })`}
                                                                data-testid={availabilityTestId}
                                                            />
                                                        ) : (
                                                            <Box
                                                                {...(isSelected
                                                                    ? selectedQuickViewStyles.swatchText
                                                                    : styles.swatchText)}
                                                                as="span"
                                                                data-testid={availabilityTestId}
                                                            >
                                                                {name}
                                                            </Box>
                                                        )

                                                        const showNotifyMeIcon = notifyMeIcon(
                                                            id,
                                                            value,
                                                            orderable
                                                        )

                                                        const fewItemsLeft = getFewItemsLeftEntry(value)

                                                        return (
                                                            <Box key={value}>
                                                                <Swatch
                                                                    isFocusable={true}
                                                                    disabled={!orderable}
                                                                    isNotifyMe={showNotifyMeIcon}
                                                                    value={value}
                                                                    name={name}
                                                                    isQuickView
                                                                    variant={
                                                                        id === 'color'
                                                                            ? 'circle'
                                                                            : 'square'
                                                                    }
                                                                    selected={isSelected}
                                                                    isQuickAdd={isQuickAdd}
                                                                    handleSelect={(v) =>
                                                                        handleSwatchChange(id, v)
                                                                    }
                                                                    id={id}
                                                                    onMouseEnter={
                                                                        id === 'color'
                                                                            ? () =>
                                                                                  setHoveredColorValue({
                                                                                      name,
                                                                                      value
                                                                                  })
                                                                            : undefined
                                                                    }
                                                                    onMouseLeave={
                                                                        id === 'color'
                                                                            ? () => setHoveredColorValue(null)
                                                                            : undefined
                                                                    }
                                                                >
                                                                    {/* envelope */}
                                                                    {showNotifyMeIcon && (
                                                                        <NotifyMeEnvelope
                                                                            {...styles.notifyMeIcon}
                                                                        />
                                                                    )}

                                                                    {!showNotifyMeIcon &&
                                                                        orderable &&
                                                                        fewItemsLeft && (
                                                                        <FewItemsLeftIcon
                                                                            {...styles.fewItemsLeftIcon}
                                                                        />
                                                                    )}
                                                                    {content}
                                                                </Swatch>
                                                            </Box>
                                                        )
                                                    }
                                                )}
                                            </Flex>
                                        </Flex>
                                    </>
                                )}
                            </Box>
                        )
                    })
                )}
            </VStack>
            {children}
        </VStack>
    )
}

QuickViewVariations.propTypes = {
    styles: PropTypes.object,
    customStyles: PropTypes.object,
    showSkeleton: PropTypes.bool,
    hasLengthVariant: PropTypes.bool,
    variationAttributes: PropTypes.array,
    isDesktop: PropTypes.bool,
    showOptionsMessage: PropTypes.bool,
    missingAttributeMessages: PropTypes.object,
    getAttributeLabel: PropTypes.func,
    isNotifyMe: PropTypes.bool,
    isFewItemsLeft: PropTypes.bool,
    variant: PropTypes.object,
    getInitialSlideIndex: PropTypes.func,
    handleSwatchChange: PropTypes.func,
    notifyMeIcon: PropTypes.func,
    product: PropTypes.object,
    onOpenSizeChart: PropTypes.func,
    children: PropTypes.node,
    optionsAttempt: PropTypes.number,
    enableAutoScroll: PropTypes.bool,
    isQuickAdd: PropTypes.string
}

export default QuickViewVariations
